import { Floor, FloorMarkerResourceDto } from '@jooxter/api';
import { useCallback, useMemo } from 'react';
import { getResourceIdsInMatrix, parseRawMatrix } from '../core/matrix';

/**
 * Same rule as ResourceLocator.canBeLocated, for a whole list of resources: each floor plan matrix
 * is parsed and scanned once instead of once per resource.
 */
export const useLocatableResources = (
  floorPlans: Floor[] = [],
  markers: FloorMarkerResourceDto[] = []
): { canBeLocated: (resourceId: number, floorId?: number) => boolean } => {
  const markerResourceIds = useMemo(
    () => new Set(markers.map((marker) => marker.resource?.id).filter((id): id is number => id !== undefined)),
    [markers]
  );

  const matrixResourceIdsByFloor = useMemo(
    () =>
      new Map<number | undefined, Set<number>>(
        floorPlans.map((floorPlan) => [floorPlan.id, getResourceIdsInMatrix(parseRawMatrix(floorPlan.matrix))])
      ),
    [floorPlans]
  );

  const canBeLocated = useCallback(
    (resourceId: number, floorId?: number) => {
      if (markers.length) {
        return markerResourceIds.has(resourceId);
      }

      return !!matrixResourceIdsByFloor.get(floorId)?.has(resourceId);
    },
    [markers.length, markerResourceIds, matrixResourceIdsByFloor]
  );

  return { canBeLocated };
};
